<template>
  <div class="diorama-host" aria-hidden="true">
    <!-- Three-phone composition: left blank, center drawing-in-progress,
         right received card. Mirrors mocks/redesign-4-soft-y2k-bubble.html
         lines 1218-1249 (.home-diorama / .dio-phone). The static frame
         (what reduced-motion users see) IS the mock — animation layered on
         top dramatizes draw → send → receive over an 8s loop. -->
    <div class="diorama">
      <!-- LEFT: empty / waiting canvas, dashed cyan crosshatch -->
      <div class="dio-phone left">
        <div class="dscreen">
          <!-- Faint "incoming…" pulse to hint the trio is one circuit -->
          <div class="left-ping"></div>
        </div>
      </div>

      <!-- RIGHT: received card from @theo with a tiny sketch -->
      <div class="dio-phone right">
        <div class="dscreen">
          <div class="received">
            <div class="received-meta">@theo · 1h</div>
            <svg
              class="received-sketch"
              width="100%"
              height="78"
              viewBox="0 0 80 70"
            >
              <path
                class="r-squiggle"
                d="M14 50 Q30 18 46 38 Q60 56 70 28"
                stroke="#f035c0"
                stroke-width="3"
                fill="none"
                stroke-linecap="round"
              />
              <circle class="r-circle" cx="22" cy="22" r="4" fill="#32c4f5" />
              <path class="r-tri" d="M52 12 L58 22 L46 22 Z" fill="#40dfab" />
            </svg>
            <!-- A ✿ that blooms once the card is received -->
            <span class="bloom">✿</span>
          </div>
        </div>
      </div>

      <!-- CENTER: live drawing-in-progress with smiley sketch + SEND ✿ -->
      <div class="dio-phone center">
        <div class="dscreen">
          <div class="tool-bar"></div>
          <svg
            class="center-sketch"
            width="100%"
            height="160"
            viewBox="0 0 110 150"
          >
            <!-- swooping head curve -->
            <path
              class="c-head"
              d="M16 90 Q20 40 50 50 Q80 60 90 30"
              stroke="#160b38"
              stroke-width="3"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <!-- eyes -->
            <circle class="c-eye-l" cx="36" cy="64" r="3" fill="#f035c0" />
            <circle class="c-eye-r" cx="60" cy="56" r="3" fill="#f035c0" />
            <!-- smile -->
            <path
              class="c-smile"
              d="M40 78 Q48 86 58 78"
              stroke="#160b38"
              stroke-width="2.5"
              fill="none"
              stroke-linecap="round"
            />
            <!-- chrome-blue accent squiggle -->
            <path
              class="c-accent"
              d="M86 110 q6 -8 14 0"
              stroke="#32c4f5"
              stroke-width="2.5"
              fill="none"
              stroke-linecap="round"
            />
          </svg>
          <div class="send-bar">
            <div class="send-pill">SEND ✿</div>
          </div>
        </div>

        <!-- Flying doodle: a holographic mini-scrawl that leaves the
             center phone and lands on the right phone during Beat 2.
             Anchored on the center phone so its starting reference is the
             SEND pill area; translateX/Y carries it into the right phone. -->
        <div class="flyer">
          <svg viewBox="0 0 24 24">
            <path
              d="M4 16 Q8 6 14 12 Q20 18 22 8"
              stroke="#f035c0"
              stroke-width="2.4"
              fill="none"
              stroke-linecap="round"
            />
            <circle cx="7" cy="7" r="1.6" fill="#32c4f5" />
          </svg>
        </div>
        <!-- Sparkle trail crumbs that follow the flyer -->
        <span class="spark s1">✦</span>
        <span class="spark s2">✦</span>
        <span class="spark s3">✦</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ScrawlDemo",
};
</script>

<style lang="scss" scoped>
/* ─── Soft Y2K three-phone diorama ───
   Pin dimensions to a 332-tall stage so Home.vue's existing
   transform/scale math (which reclaims `(1 - scale) × 332` of empty
   layout space below the diorama) keeps working untouched.

   Layout mirrors mock screen 01 (.home-diorama, .dio-phone, .dscreen).
   Three little candy phones: left rotated -15deg with an empty hatched
   canvas, right rotated +15deg with an @theo received card, center
   upright in front showing a smiley-face drawing-in-progress with a
   SEND ✿ pill at the bottom.

   ─── Choreography (single 8s loop) ───
     0 – 25%   BEAT 1  DRAW       smiley strokes itself onto center
     25 – 45%  BEAT 2  SEND       SEND pill pulses + glows, flyer leaves
     45 – 70%  BEAT 3  RECEIVE    right card pops in, sketch self-draws
     70 – 95%  BEAT 4  RESET      everything fades, shimmer sweeps trio
     95 – 100% BEAT 5  HOLD       static rest matching the mock
   The gentle 6s bob runs underneath and harmonizes with the 8s narrative.
*/

.diorama-host {
  width: 100%;
  max-width: 342px;
  margin: 0 auto;
  user-select: none;
  -webkit-user-select: none;
}

.diorama {
  position: relative;
  width: 100%;
  height: 332px;
}

/* ─── Phone bodies ───
   Soft white→lilac body with a candy ambient drop and a top inset
   highlight so each reads as molded plastic rather than a flat card. */
.dio-phone {
  position: absolute;
  width: 130px;
  height: 230px;
  border-radius: 24px;
  background: linear-gradient(160deg, #ffffff, #f0e0ff);
  border: 1px solid rgba(255, 255, 255, 0.9);
  padding: 8px;
  overflow: visible; /* allow flyer + sparkles to escape the center body */
  box-shadow:
    0 20px 40px -20px rgba($shadow-purple, 0.5),
    inset 0 1px 0 #fff;
  will-change: transform;

  &.left {
    left: 8px;
    top: 30px;
    transform: rotate(-15deg);
    animation: dio-bobL 6s ease-in-out infinite;
    z-index: 1;
  }
  &.right {
    right: 8px;
    top: 30px;
    transform: rotate(15deg);
    animation: dio-bobR 6.4s ease-in-out infinite;
    z-index: 1;
  }
  &.center {
    left: 50%;
    top: 0;
    transform: translateX(-50%);
    z-index: 3; /* flyer launches from here, must sit above siblings */
    animation: dio-bobC 5.6s ease-in-out infinite;
  }
}

/* Gentle bob — runs under the narrative so the diorama always feels
   like it's breathing even between beats. */
@keyframes dio-bobL {
  0%, 100% { transform: rotate(-15deg) translateY(0); }
  50%      { transform: rotate(-13.5deg) translateY(-3px); }
}
@keyframes dio-bobR {
  0%, 100% { transform: rotate(15deg) translateY(0); }
  50%      { transform: rotate(16.5deg) translateY(-3px); }
}
@keyframes dio-bobC {
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50%      { transform: translateX(-50%) translateY(-2px); }
}

/* ─── Inner screens ───
   Each screen carries its own subtle candy-sky gradient. The left
   screen shows a dashed cyan hatch (waiting/empty), the right has a
   warmer pink wash (received), and the center stays neutral so the
   sketch reads cleanly. The shared shimmer sweeps each screen once
   per loop during BEAT 4 (reset). */
.dio-phone .dscreen {
  width: 100%;
  height: 100%;
  border-radius: 18px;
  overflow: hidden;
  position: relative;
  background: linear-gradient(180deg, #dff0ff, #ffe4f1, $mint-tint);
}

/* Shared shimmer sweep — a diagonal highlight ripples across each
   phone's inner screen in BEAT 4 to reinforce "the cycle is closing". */
.dio-phone .dscreen::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    115deg,
    transparent 30%,
    rgba(255, 255, 255, 0.55) 50%,
    transparent 70%
  );
  transform: translateX(-130%);
  pointer-events: none;
  animation: dio-shimmer 8s ease-in-out infinite;
}
.dio-phone.left  .dscreen::before { animation-delay: 0s; }
.dio-phone.center .dscreen::before { animation-delay: 0.08s; }
.dio-phone.right .dscreen::before { animation-delay: 0.16s; }
@keyframes dio-shimmer {
  0%, 70%   { transform: translateX(-130%); opacity: 0; }
  74%       { opacity: 1; }
  86%       { transform: translateX(130%);  opacity: 1; }
  90%, 100% { transform: translateX(130%);  opacity: 0; }
}

.dio-phone.left .dscreen::after {
  content: "";
  position: absolute;
  inset: 18px;
  border-radius: 14px;
  background: repeating-linear-gradient(
    45deg,
    transparent 0 6px,
    rgba($chrome-blue, 0.18) 6px 7px
  );
  border: 1px dashed rgba($chrome-blue, 0.5);
}

.dio-phone.right .dscreen {
  background: linear-gradient(180deg, #ffeaf6, #f8a5d4);
}

/* Left phone "ping" — a faint cyan ring breathes once per loop, hinting
   that the left silo is part of the same conversation circuit. */
.left-ping {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 40px;
  height: 40px;
  margin: -20px 0 0 -20px;
  border-radius: 50%;
  border: 1.5px solid rgba($chrome-blue, 0.7);
  opacity: 0;
  transform: scale(0.6);
  animation: dio-leftPing 8s $ease-soft infinite;
  pointer-events: none;
  z-index: 2;
}
@keyframes dio-leftPing {
  0%, 60%   { opacity: 0; transform: scale(0.6); }
  64%       { opacity: 0.8; transform: scale(0.9); }
  78%       { opacity: 0; transform: scale(1.6); }
  100%      { opacity: 0; transform: scale(0.6); }
}

/* ─── Right phone: received card from @theo ─── */
.received {
  position: absolute;
  inset: 14px;
  border-radius: 14px;
  background: #fff;
  border: 1px dashed #ffb3df;
  padding: 6px;
  box-sizing: border-box;
  transform-origin: 50% 60%;
  /* Card itself pops in at BEAT 3, then eases out at BEAT 4. */
  animation: dio-receivedCard 8s $ease-bubble infinite;
}
@keyframes dio-receivedCard {
  0%, 44%   { opacity: 0; transform: scale(0.4) rotate(-4deg); }
  48%       { opacity: 1; transform: scale(1.08) rotate(0deg); }
  54%       { opacity: 1; transform: scale(0.97) rotate(0deg); }
  58%       { opacity: 1; transform: scale(1) rotate(0deg); }
  92%       { opacity: 1; transform: scale(1) rotate(0deg); }
  96%, 100% { opacity: 0; transform: scale(0.85) rotate(-2deg); }
}

.received-meta {
  font-size: 7px;
  font-weight: 700;
  color: #a445a8;
  letter-spacing: 0.02em;
}
.received-sketch {
  display: block;
  margin-top: 4px;
  overflow: visible;
}

/* Each shape inside the received sketch self-draws as part of BEAT 3.
   The squiggle uses dasharray/offset; the circle and triangle scale
   in with a slight stagger so the shapes feel "placed" one by one. */
.r-squiggle {
  stroke-dasharray: 120;
  stroke-dashoffset: 120;
  animation: dio-rSquiggle 8s $ease-soft infinite;
}
@keyframes dio-rSquiggle {
  0%, 48%   { stroke-dashoffset: 120; }
  62%       { stroke-dashoffset: 0; }
  92%       { stroke-dashoffset: 0; }
  96%, 100% { stroke-dashoffset: 120; }
}
.r-circle {
  transform-origin: 22px 22px;
  transform: scale(0);
  animation: dio-rCircle 8s $ease-bubble infinite;
}
@keyframes dio-rCircle {
  0%, 56%   { transform: scale(0); }
  62%       { transform: scale(1.3); }
  66%       { transform: scale(1); }
  92%       { transform: scale(1); }
  96%, 100% { transform: scale(0); }
}
.r-tri {
  transform-origin: 52px 17px;
  transform: scale(0);
  animation: dio-rTri 8s $ease-bubble infinite;
}
@keyframes dio-rTri {
  0%, 60%   { transform: scale(0); }
  66%       { transform: scale(1.25); }
  70%       { transform: scale(1); }
  92%       { transform: scale(1); }
  96%, 100% { transform: scale(0); }
}

/* ✿ that blooms beside the card content once the message has landed. */
.bloom {
  position: absolute;
  bottom: 6px;
  right: 8px;
  font-size: 14px;
  color: $bubblegum;
  text-shadow: 0 0 6px rgba($bubblegum, 0.6);
  opacity: 0;
  transform: scale(0) rotate(-90deg);
  animation: dio-bloom 8s $ease-bubble infinite;
}
@keyframes dio-bloom {
  0%, 60%   { opacity: 0; transform: scale(0) rotate(-90deg); }
  68%       { opacity: 1; transform: scale(1.3) rotate(20deg); }
  74%       { opacity: 1; transform: scale(1) rotate(0deg); }
  90%       { opacity: 1; transform: scale(1) rotate(0deg); }
  96%, 100% { opacity: 0; transform: scale(0.4) rotate(-30deg); }
}

/* ─── Center phone: drawing-in-progress ─── */
.tool-bar {
  position: absolute;
  top: 14px;
  left: 14px;
  right: 14px;
  height: 8px;
  border-radius: 4px;
  background: rgba($bubblegum, 0.2);
}
.center-sketch {
  display: block;
  margin-top: 30px;
  position: relative;
  z-index: 1;
  overflow: visible;
}

/* BEAT 1 self-draw: each stroke / dot layers on with a small stagger so
   the smiley feels hand-drawn. The whole sketch fades during BEAT 4 so
   the center returns to a clean canvas before the loop restarts. */
.c-head {
  stroke-dasharray: 160;
  stroke-dashoffset: 160;
  animation:
    dio-cHead 8s $ease-soft infinite,
    dio-cFade 8s $ease-soft infinite;
}
@keyframes dio-cHead {
  0%   { stroke-dashoffset: 160; }
  20%  { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: 0; }
}
.c-eye-l, .c-eye-r {
  transform-origin: center;
  transform: scale(0);
  animation:
    dio-cEye 8s $ease-bubble infinite,
    dio-cFade 8s $ease-soft infinite;
}
.c-eye-l { transform-origin: 36px 64px; }
.c-eye-r { transform-origin: 60px 56px; animation-delay: 0.1s, 0s; }
@keyframes dio-cEye {
  0%, 12%   { transform: scale(0); }
  18%       { transform: scale(1.4); }
  22%       { transform: scale(1); }
  100%      { transform: scale(1); }
}
.c-smile {
  stroke-dasharray: 28;
  stroke-dashoffset: 28;
  animation:
    dio-cSmile 8s $ease-soft infinite,
    dio-cFade 8s $ease-soft infinite;
}
@keyframes dio-cSmile {
  0%, 16%   { stroke-dashoffset: 28; }
  24%       { stroke-dashoffset: 0; }
  100%      { stroke-dashoffset: 0; }
}
.c-accent {
  stroke-dasharray: 22;
  stroke-dashoffset: 22;
  animation:
    dio-cAccent 8s $ease-soft infinite,
    dio-cFade 8s $ease-soft infinite;
}
@keyframes dio-cAccent {
  0%, 20%   { stroke-dashoffset: 22; }
  26%       { stroke-dashoffset: 0; }
  100%      { stroke-dashoffset: 0; }
}
/* Shared fade — after the send, the sketch gradually ghosts out as the
   message lives on the receiver instead of the sender's canvas. */
@keyframes dio-cFade {
  0%, 35%   { opacity: 1; }
  45%       { opacity: 0.6; }
  85%       { opacity: 0.6; }
  92%, 100% { opacity: 0; }
}

.send-bar {
  position: absolute;
  bottom: 14px;
  left: 14px;
  right: 14px;
  z-index: 2;
}
.send-pill {
  position: relative;
  height: 30px;
  border-radius: 14px;
  background: $holo-linear;
  background-size: 220% 100%;
  background-position: 0% 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #fff;
  font-weight: 800;
  letter-spacing: 0.04em;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.7),
    0 4px 10px -2px rgba($bubblegum, 0.5);
  /* BEAT 2: pulse + glow + slide the holographic gradient. */
  animation: dio-sendPulse 8s $ease-bubble infinite;
}
@keyframes dio-sendPulse {
  0%, 24%  {
    transform: scale(1);
    background-position: 0% 50%;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.7),
      0 4px 10px -2px rgba($bubblegum, 0.5);
  }
  30%      {
    transform: scale(1.08);
    background-position: 100% 50%;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.9),
      0 6px 22px -2px rgba($bubblegum, 0.85),
      0 0 18px rgba($chrome-blue, 0.55);
  }
  36%      {
    transform: scale(0.94);
    background-position: 100% 50%;
  }
  44%      {
    transform: scale(1);
    background-position: 50% 50%;
  }
  100%     {
    transform: scale(1);
    background-position: 0% 50%;
  }
}

/* ─── Flyer: a tiny holographic doodle launches from the center phone
   and arcs to the right phone during BEAT 2 (~28% – 50% of the loop).
   Anchored on the center phone so origin (0,0) sits at center-top —
   keyframes carry it down to the SEND pill, then up + right toward the
   received-card on the right phone. */
.flyer {
  position: absolute;
  top: 180px;     /* level with the SEND pill */
  left: 50%;
  width: 28px;
  height: 28px;
  margin-left: -14px;
  pointer-events: none;
  z-index: 4;
  opacity: 0;
  transform: translate(0, 0) scale(0.4) rotate(0deg);
  filter: drop-shadow(0 4px 8px rgba($bubblegum, 0.55));
  animation: dio-flyer 8s $ease-soft infinite;

  svg { width: 100%; height: 100%; overflow: visible; }
}
@keyframes dio-flyer {
  /* Hidden during BEAT 1 */
  0%, 26%  {
    opacity: 0;
    transform: translate(0, 0) scale(0.4) rotate(0deg);
  }
  /* Pop out of the SEND pill */
  30%      {
    opacity: 1;
    transform: translate(0, -4px) scale(1.15) rotate(-6deg);
  }
  /* Arc up + right toward the right phone (mid-flight apex) */
  40%      {
    opacity: 1;
    transform: translate(70px, -70px) scale(0.95) rotate(12deg);
  }
  /* Land on the right phone's card */
  48%      {
    opacity: 1;
    transform: translate(115px, -30px) scale(0.7) rotate(20deg);
  }
  /* Dissolve into the card */
  52%      {
    opacity: 0;
    transform: translate(115px, -30px) scale(0.4) rotate(20deg);
  }
  100%     {
    opacity: 0;
    transform: translate(0, 0) scale(0.4) rotate(0deg);
  }
}

/* Sparkle crumbs trail behind the flyer with staggered fades. */
.spark {
  position: absolute;
  top: 180px;
  left: 50%;
  font-size: 10px;
  color: $lilac;
  pointer-events: none;
  z-index: 4;
  opacity: 0;
  text-shadow: 0 0 4px rgba(182, 156, 255, 0.7);
  animation: dio-spark 8s $ease-soft infinite;
}
.spark.s1 { animation-delay: 0.15s; color: $bubblegum; }
.spark.s2 { animation-delay: 0.30s; color: $chrome-blue; }
.spark.s3 { animation-delay: 0.45s; color: $mint; }
@keyframes dio-spark {
  0%, 28%  { opacity: 0; transform: translate(0, 0) scale(0.6); }
  32%      { opacity: 1; transform: translate(15px, -18px) scale(1); }
  42%      { opacity: 0.8; transform: translate(60px, -55px) scale(0.9); }
  50%      { opacity: 0; transform: translate(105px, -28px) scale(0.4); }
  100%     { opacity: 0; transform: translate(0, 0) scale(0.6); }
}

/* ─── Reduced motion: hold the static mock frame ─── */
@media (prefers-reduced-motion: reduce) {
  .dio-phone.left,
  .dio-phone.right,
  .dio-phone.center,
  .dio-phone .dscreen::before,
  .left-ping,
  .received,
  .r-squiggle, .r-circle, .r-tri,
  .bloom,
  .c-head, .c-eye-l, .c-eye-r, .c-smile, .c-accent,
  .send-pill,
  .flyer,
  .spark {
    animation: none !important;
  }
  /* Pin every animated element to its resting / mock state */
  .received { opacity: 1; transform: scale(1) rotate(0deg); }
  .r-squiggle { stroke-dashoffset: 0; }
  .r-circle, .r-tri { transform: scale(1); }
  .bloom { opacity: 0; }
  .c-head, .c-smile, .c-accent { stroke-dashoffset: 0; }
  .c-head, .c-eye-l, .c-eye-r, .c-smile, .c-accent { opacity: 1; }
  .c-eye-l, .c-eye-r { transform: scale(1); }
  .flyer, .spark, .left-ping { opacity: 0; }
  .send-pill { transform: scale(1); background-position: 0% 50%; }
}
</style>
